import { useEffect } from 'react'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAvailableRoles } from '@/hooks/queries/useMemberResourcesQuery'
import { InfoIcon, StarIcon, WarningCircleIcon } from '@phosphor-icons/react'

import { BillingConfigurationEnforcementModeEnum } from '@scrambl/shared'

interface RoleSelectorProps {
  memberId: string
  projectId?: string | null
  entityId?: string | null
  value: string | null | undefined
  onChange: (roleId: string | null) => void
  disabled?: boolean
  disabledReason?: string
  compact?: boolean
  label?: string
  showLabel?: boolean
  /** When true, adds an "Any role" option that sets roleId to null. Used in RateFormDialog. */
  showAnyOption?: boolean
  /** When true, hides the selector entirely if only one role is available (still auto-fills the value). */
  hideWhenSingle?: boolean
  showWarnings?: boolean
  enforcementMode?: BillingConfigurationEnforcementModeEnum
  onAvailableRoleCountChange?: (count: number | null) => void
}

export default function RoleSelector({
  memberId,
  projectId,
  entityId,
  value,
  onChange,
  disabled = false,
  disabledReason,
  compact = false,
  label = 'Billing role',
  showLabel = true,
  showAnyOption = false,
  hideWhenSingle = false,
  showWarnings = true,
  enforcementMode = BillingConfigurationEnforcementModeEnum.WARN_ONLY,
  onAvailableRoleCountChange,
}: RoleSelectorProps) {
  const { data: availableRoles, isPending } = useAvailableRoles(
    {
      memberId,
      projectId: projectId ?? undefined,
      entityId: projectId ? undefined : (entityId ?? undefined),
    },
    { enabled: !!memberId },
  )
  // Scoped isPending, not isLoading: a query waiting on the org or the
  // persisted cache is not fetching yet, and isLoading would report zero roles
  // and show the base-rate fallback; with no member the query never runs.
  const isLoading = !!memberId && isPending

  useEffect(() => {
    onAvailableRoleCountChange?.(
      isLoading ? null : (availableRoles?.length ?? 0),
    )
  }, [availableRoles, isLoading, onAvailableRoleCountChange])

  // Auto-fill: single role → set it. Multi-role members must explicitly choose
  // their billing role, or intentionally leave it blank to fall back to their
  // primary role (surfaced via the inline warning below). Preselecting the
  // primary role would silently mask the fallback and is not allowed here.
  useEffect(() => {
    if (!availableRoles || disabled || value) return

    if (availableRoles.length === 1 && !showAnyOption) {
      onChange(availableRoles[0].roleId)
    }
  }, [availableRoles, value, disabled, onChange, showAnyOption])

  if (isLoading) {
    return compact ? (
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-sm text-muted-foreground">{label}:</span>
        <Skeleton className="h-8 flex-1" />
      </div>
    ) : (
      <div className="space-y-2">
        {showLabel && <Label>{label}</Label>}
        <Skeleton className="h-9 w-full" />
      </div>
    )
  }

  // No roles available — the rate resolver falls back to the member's base rate.
  if (!availableRoles || availableRoles.length === 0) {
    if (showWarnings && (projectId || entityId)) {
      return (
        <div
          className="flex items-start gap-1.5 text-xs text-muted-foreground"
          role="status"
          data-testid="role-base-rate-fallback-status"
        >
          <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Your base billing rate will be used for this entry.</span>
        </div>
      )
    }
    return null
  }

  // Single role without "Any role" option — show as read-only text (no selector needed)
  if (availableRoles.length === 1 && !showAnyOption) {
    // When hideWhenSingle is true, hide the UI entirely (auto-fill still runs via useEffect above)
    if (hideWhenSingle) {
      return null
    }
    const roleName = availableRoles[0].role?.name ?? 'Unknown role'
    if (compact) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <span className="shrink-0 text-muted-foreground">{label}:</span>
          <span className="font-medium">{roleName}</span>
        </div>
      )
    }
    return (
      <div className="space-y-2">
        {showLabel && <Label>{label}</Label>}
        <p className="py-2 text-sm font-medium">{roleName}</p>
      </div>
    )
  }

  // Multiple roles (or single role with "Any role" option) — show dropdown
  const selectValue = value ?? (showAnyOption ? 'none' : undefined)
  const handleChange = (v: string) => onChange(v === 'none' ? null : v)

  const primaryRole = availableRoles.find(r => r.isPrimary)
  const showPrimaryFallbackWarning =
    showWarnings && availableRoles.length > 1 && !value && !showAnyOption
  const primaryFallbackMessage =
    enforcementMode === BillingConfigurationEnforcementModeEnum.HARD_BLOCK
      ? 'Select a billing role before saving billable time.'
      : enforcementMode === BillingConfigurationEnforcementModeEnum.SUBMIT_BLOCK
        ? 'Select a billing role before submitting this entry.'
        : primaryRole?.role?.name
          ? `No role was selected. This entry will use your primary role (${primaryRole.role.name}) for rate resolution.`
          : 'No role was selected. This entry will use your primary role for rate resolution.'

  const roleItems = availableRoles.map(assignment => (
    <SelectItem key={assignment.roleId} value={assignment.roleId}>
      <span className="flex min-w-0 items-center gap-1.5">
        {assignment.isPrimary && (
          <StarIcon
            className="h-3 w-3 shrink-0 text-yellow-500"
            weight="fill"
          />
        )}
        <span className="truncate">
          {assignment.role?.name ?? 'Unknown role'}
        </span>
        {assignment.isPrimary && (
          <span className="shrink-0 text-xs text-muted-foreground">
            (primary)
          </span>
        )}
      </span>
    </SelectItem>
  ))

  const selectTrigger = (
    <SelectTrigger className={compact ? 'h-8 flex-1' : undefined}>
      <SelectValue
        placeholder={compact ? 'Select billing role…' : 'Select billing role…'}
      />
    </SelectTrigger>
  )

  const selectContent = (
    <SelectContent>
      {showAnyOption && (
        <SelectItem value="none">
          <span className="text-muted-foreground">Any role</span>
        </SelectItem>
      )}
      {roleItems}
    </SelectContent>
  )

  const fallbackWarning = showPrimaryFallbackWarning ? (
    <div
      className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400"
      role="status"
      data-testid="role-primary-fallback-warning"
    >
      <WarningCircleIcon
        className="mt-0.5 h-3.5 w-3.5 shrink-0"
        weight="fill"
      />
      <span>{primaryFallbackMessage}</span>
    </div>
  ) : null

  if (compact) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-sm text-muted-foreground">
            {label}:
          </span>
          <Select
            value={selectValue}
            onValueChange={handleChange}
            disabled={disabled}
          >
            {disabled && disabledReason ? (
              <Tooltip>
                <TooltipTrigger asChild>{selectTrigger}</TooltipTrigger>
                <TooltipContent>{disabledReason}</TooltipContent>
              </Tooltip>
            ) : (
              selectTrigger
            )}
            {selectContent}
          </Select>
        </div>
        {fallbackWarning}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {showLabel && <Label>{label}</Label>}
      <Select
        value={selectValue}
        onValueChange={handleChange}
        disabled={disabled}
      >
        {disabled && disabledReason ? (
          <Tooltip>
            <TooltipTrigger asChild>{selectTrigger}</TooltipTrigger>
            <TooltipContent>{disabledReason}</TooltipContent>
          </Tooltip>
        ) : (
          selectTrigger
        )}
        {selectContent}
      </Select>
      {fallbackWarning}
    </div>
  )
}
